/** One new starter on the list: who, when, with whom, how far — and whether it is your move. */

import { Badge, Card, Typography } from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { StarterCase } from "@/lib/api";

import { startDateValue } from "../lib/starters";
import { StageTracker } from "./stage-tracker";

export interface StarterCardProps {
  item: StarterCase;
  /** A badge before the name: the Access list's "New" type badge (5 Oct). */
  typeBadge?: ReactNode;
}

export function StarterCard({ item, typeBadge }: StarterCardProps) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const start = startDateValue(item.start_date);
  const hasAction = (item.actions ?? []).length > 0;

  return (
    <Card
      variant="outline"
      padding="md"
      accent={hasAction ? "left" : undefined}
      accentTone={hasAction ? "accent" : undefined}
      className="ease-standard transition hover:shadow-md"
    >
      <Link
        to={`/starters/${encodeURIComponent(item.case_id)}`}
        className="flex min-w-0 flex-col gap-2 rounded-md"
      >
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <Typography as="span" variant="heading-6" className="min-w-0 break-words">
            {item.new_user_name}
          </Typography>
          {typeBadge}
          {hasAction ? (
            <Badge tone="accent" variant="solid" size="xs">
              {t("starters.actionForYou")}
            </Badge>
          ) : null}
          {item.is_open ? null : (
            <Badge tone="neutral" variant="subtle" size="xs">
              {item.nur_status}
            </Badge>
          )}
        </span>
        <Typography as="span" variant="body-sm" tone="muted">
          {start === null
            ? t("starters.startDateUnknown")
            : t("starters.startDate", { date: formatDate(start) })}
          {item.job_title ? ` · ${item.job_title}` : ""}
          {item.manager?.display_name
            ? ` · ${t("starters.managerLine", { name: item.manager.display_name })}`
            : ""}
        </Typography>
        <StageTracker stages={item.stages ?? []} compact />
      </Link>
    </Card>
  );
}
